if (!customElements.get('hero-banner-slideshow')) {
  customElements.define('hero-banner-slideshow', class extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.slides = [...this.querySelectorAll('[data-hero-slide]')];
      this.contents = [...this.querySelectorAll('[data-hero-content]')];
      this.dots = [...this.querySelectorAll('[data-hero-dot]')];
      this.rotationButton = this.querySelector('[data-hero-rotation]');
      this.status = this.querySelector('[data-hero-status]');
      this.current = 0;
      this.inViewport = true;
      this.hovered = false;
      this.editorSelected = false;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.mobile = matchMedia('(max-width: 749px)');
      this.paused = this.motion.matches || Boolean(window.Shopify?.designMode);
      if (!this.slides.length) return;

      const controls = this.querySelector('[data-hero-controls]');
      if (controls) controls.hidden = this.slides.length < 2;

      this.addEventListener('click', (event) => {
        const button = event.target.closest('button');
        if (!button) return;
        if (button.hasAttribute('data-hero-prev')) this.select(this.current - 1, true);
        if (button.hasAttribute('data-hero-next')) this.select(this.current + 1, true);
        if (button.hasAttribute('data-hero-dot')) this.select(Number(button.dataset.heroDot), true);
        if (button === this.rotationButton) {
          this.paused = !this.paused;
          this.updateRotation();
        }
      }, { signal });

      this.addEventListener('keydown', (event) => {
        if (!event.target.closest('[data-hero-controls]')) return;
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const direction = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
        this.select(this.current + (event.key === 'ArrowRight' ? direction : -direction), true);
      }, { signal });

      this.addEventListener('pointerdown', (event) => {
        this.touchStart = event.isPrimary && event.pointerType === 'touch'
          ? { x: event.clientX, y: event.clientY }
          : null;
      }, { signal });
      this.addEventListener('pointerup', (event) => {
        if (!this.touchStart) return;
        const dx = event.clientX - this.touchStart.x;
        const dy = event.clientY - this.touchStart.y;
        this.touchStart = null;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          const direction = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
          this.select(this.current + (dx < 0 ? direction : -direction), true);
        }
      }, { signal });
      this.addEventListener('pointercancel', () => { this.touchStart = null; }, { signal });

      this.addEventListener('mouseenter', () => {
        this.hovered = true;
        this.updateRotation();
      }, { signal });
      this.addEventListener('mouseleave', () => {
        this.hovered = false;
        this.updateRotation();
      }, { signal });
      // Keyboard focus stops rotation until the visitor explicitly resumes it.
      this.addEventListener('focusin', (event) => {
        if (event.target === this.rotationButton) return;
        this.paused = true;
        this.updateRotation();
      }, { signal });

      document.addEventListener('visibilitychange', () => {
        this.updateVideos();
        this.updateRotation();
      }, { signal });
      this.mobile.addEventListener('change', () => this.updateVideos(), { signal });
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.paused = true;
        this.updateVideos();
        this.updateRotation();
      }, { signal });

      document.addEventListener('shopify:block:select', (event) => {
        const index = this.slides.findIndex((slide) => slide.dataset.blockId === event.detail?.blockId);
        if (index < 0) return;
        this.editorSelected = true;
        this.select(index);
      }, { signal });
      document.addEventListener('shopify:block:deselect', (event) => {
        if (!this.slides.some((slide) => slide.dataset.blockId === event.detail?.blockId)) return;
        this.editorSelected = false;
        this.updateRotation();
      }, { signal });

      this.observer = new IntersectionObserver(([entry]) => {
        this.inViewport = entry.isIntersecting;
        this.updateVideos();
        this.updateRotation();
      });
      this.observer.observe(this);
      this.select(0);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.observer?.disconnect();
      clearTimeout(this.timer);
      this.querySelectorAll('video').forEach((video) => video.pause());
    }

    select(index, userInitiated = false) {
      this.current = (index + this.slides.length) % this.slides.length;
      if (userInitiated) this.paused = true;
      const contentId = this.slides[this.current].dataset.contentId;
      this.slides.forEach((slide, i) => this.setActive(slide, i === this.current));
      this.contents.forEach((content) => this.setActive(content, content.dataset.heroContent === contentId));
      this.dots.forEach((dot, i) => {
        if (i === this.current) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (this.status && userInitiated) this.status.textContent = `Slide ${this.current + 1} of ${this.slides.length}`;
      this.updateVideos();
      this.updateRotation();
    }

    setActive(element, active) {
      element.classList.toggle('is-active', active);
      element.inert = !active;
      if (active) element.removeAttribute('aria-hidden');
      else element.setAttribute('aria-hidden', 'true');
    }

    updateVideos() {
      this.slides.forEach((slide, index) => {
        slide.querySelectorAll('video').forEach((video) => {
          const correctSize = !(video.classList.contains('is-mobile') && !this.mobile.matches)
            && !(video.classList.contains('is-desktop') && this.mobile.matches);
          const shouldPlay = index === this.current && correctSize && this.inViewport
            && !document.hidden && !this.motion.matches;
          if (shouldPlay) video.play()?.catch(() => {});
          else video.pause();
        });
      });
    }

    updateRotation() {
      clearTimeout(this.timer);
      if (this.rotationButton) {
        this.rotationButton.setAttribute('aria-label', this.paused ? 'Play slideshow' : 'Pause slideshow');
        this.rotationButton.querySelector('[data-hero-play-icon]').toggleAttribute('hidden', !this.paused);
        this.rotationButton.querySelector('[data-hero-pause-icon]').toggleAttribute('hidden', this.paused);
      }
      if (this.dataset.autoplay !== 'true' || this.slides.length < 2 || this.paused
        || this.hovered || this.editorSelected || !this.inViewport || document.hidden) return;
      const interval = Math.max(3000, Number(this.dataset.interval) || 6000);
      this.timer = setTimeout(() => this.select(this.current + 1), interval);
    }
  });
}
